import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { getEnv } from "mobx-easy";
import { RootEnv } from "stores/setup/create-store";
import { useStores } from "stores/setup/use-store";
import { EditEventFormValues, EventEditData, getEventEditInitialValues, InsightColorId } from "../types";
import { SensorAlertConfig } from "types/ISensorAlertConfig";
import { ILocationMenuInsight } from "stores/data/operationRuleLocations/OperationRuleLocationModel";
import { useAlertCreationUsers } from "pages/Alert/hooks/useAlertCreationUsers";
import { ConditionGroup } from "stores/data/insightAlerts/insightAlertModel";
import { conditionGroupsSchema } from "pages/Alert/util/conditionGroupsSchema";
import {
    DEFAULT_MIN_ADVANCE_TIME,
    LEGACY_MAX_ADVANCE_TIME_FALLBACK,
} from "pages/OceanWeatherIntelligence/InsightAlert/constants/SendConfigConstants";
import { GetEventByIdResponse } from "services/EventService.types";
import { RISK_TYPES, RiskTypeId } from "pages/Alert/components/AlertTypeSelector";

interface UseEventEditDrawerParams {
    isOpen: boolean;
    onClose: () => void;
    eventToEdit: ILocationMenuInsight | null;
    locationId: number | null;
    locationName: string;
    dashboardId: number;
    /** When true the drawer is reopening after the user confirmed a new sensor
     *  selection in Forecast Charts. loadEventDetails skips overwriting
     *  insightAlertStore.selectedSensorIds so the new selection is preserved. */
    isReturningFromSensorSelection?: boolean;
}

/**
 * Hook for managing Event edit drawer state and logic
 * Handles loading event data via GET /v1/event/:id, form validation, and submission
 */
export function useEventEditDrawer({
    isOpen,
    onClose,
    eventToEdit,
    locationId,
    locationName,
    dashboardId,
    isReturningFromSensorSelection = false,
}: UseEventEditDrawerParams) {
    const {
        dataStores: {
            operationRuleStore,
            unitMeasurementStore,
            insightAlertStore,
        },
    } = useStores();

    const { eventService } = getEnv<RootEnv>();

    const { t, i18n } = useTranslation("translation", {
        keyPrefix: "event",
    });

    // Reuse hooks from Alert module
    // skipAutoSelect: In edit mode, recipients come from the API response,
    // so we don't want auto-select to override them with just the current user.
    const users = useAlertCreationUsers({ skipAutoSelect: true });

    // Loading state
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Store raw event data from API
    const [eventData, setEventData] = useState<GetEventByIdResponse | null>(null);

    // Form initial values
    const [initialValues, setInitialValues] = useState<EditEventFormValues>(() => ({
        name: "",
        alertType: "extreme_wind",
        channels: ["whatsapp"],
        selectedUsers: [],
        customTitle: "",
        customDescription: "",
        resume: "",
        minAdvanceTime: DEFAULT_MIN_ADVANCE_TIME,
        maxAdvanceTime: LEGACY_MAX_ADVANCE_TIME_FALLBACK,
        minimumEventDuration: 60,
        receiptType: ["sendUpdates", "sendCancellations"],
        conditionGroups: [],
        block1Active: true,
        block2Active: false,
        restrictionType: true,
        insightColor: 1,
    }));

    // Validation schema
    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                name: Yup.string().required(t("nameRequired") as string),
                conditionGroups: conditionGroupsSchema,
                // Block 1 validation
                dashboardId: Yup.number().when("block1Active", {
                    is: true,
                    then: (schema) => schema.required(t("dashboardRequired") as string),
                }),
                // Block 2 validation (channels, users, etc.)
                channels: Yup.array().when("block2Active", {
                    is: true,
                    then: (schema) =>
                        schema.min(1, t("channelRequired") as string),
                }),
                selectedUsers: Yup.array().when("block2Active", {
                    is: true,
                    then: (schema) =>
                        schema.min(1, t("userRequired") as string),
                }),
                sensorAlertConfig: Yup.object().nullable().when({
                    is: (val: any) => val && val.sensors && val.sensors.length > 0,
                    then: () => Yup.object().shape({
                        conditions: conditionGroupsSchema,
                    }),
                    otherwise: () => Yup.object().nullable(),
                }),
            }),
        [t]
    );

    /**
     * Maps GET /v1/event/:id sensorAlertConfig to SensorAlertConfig form shape.
     * The API response sensors include stationName (resolved via JOIN), which the
     * form needs for display in SensorSelector.
     */
    const mapSensorAlertConfig = useCallback((
        response: GetEventByIdResponse
    ): SensorAlertConfig | undefined => {
        if (!response.sensorAlertConfig) return undefined;
        const { sensors, alertFrequency, lightning } = response.sensorAlertConfig;

        const hasRain = !!sensors && sensors.length > 0;
        const hasLightning = !!lightning;
        if (!hasRain && !hasLightning) return undefined;

        // Conditions are stored per-sensor in the API response; all sensors share the
        // same conditions, so we read from the first sensor.
        const conditions = hasRain ? sensors[0].conditions || [] : [];

        return {
            sensors: hasRain
                ? sensors.map((s) => ({
                    stationId: s.stationId,
                    regionId: s.regionId,
                    stationName: s.stationName ?? "",
                }))
                : [],
            conditions,
            alertFrequency: alertFrequency ?? 60,
            // Spec 043 — hydrate lightning slice; preserved verbatim until the user
            // moves the point or clears the slice via Popconfirm.
            lightning: hasLightning ? lightning : undefined,
        };
    }, []);

    /**
     * Maps API response to ConditionGroup[] format used by the form
     */
    const mapConditionGroups = useCallback((response: GetEventByIdResponse): ConditionGroup[] => {
        // conditionGroups is at root level of response
        if (!response.conditionGroups || response.conditionGroups.length === 0) {
            return [];
        }

        return response.conditionGroups.map((group, index) => ({
            id: group.id ? String(group.id) : String(index),
            parentId: group.parentId != null ? String(group.parentId) : null,
            groupOperator: (group.groupOperator || "and").toLowerCase(),
            conditionOperator: (group.conditionOperator || "and").toLowerCase(),
            conditions: group.conditions?.map(c => ({
                value: c.value,
                comparisonOperator: c.comparisonOperator,
                environmentalVariable: c.environmentalVariable,
                environmentalVariableId: c.environmentalVariableId,
            })) || [],
        }));
    }, []);

    /**
     * Maps API response to EditEventFormValues
     */
    const mapResponseToFormValues = useCallback((
        response: GetEventByIdResponse,
        dashboard?: { id: number; name: string },
        locationNameFromProps?: string
    ): EditEventFormValues => {
        const { insight, alert } = response;
        const conditionGroups = mapConditionGroups(response);

        // Build EventEditData structure
        // Note: name, conditionGroups are at root level of response
        const eventEditData: EventEditData = {
            insightId: insight.id,
            insightName: response.name, // name is at root level
            locationId: insight.operationRuleLocationId,
            locationName: locationNameFromProps || "", // Use locationName from props
            dashboardId: insight.dashboardId,
            dashboardName: dashboard?.name || "",
            conditions: conditionGroups,
            restrictionType: insight.restrictionType ?? true,
            priorityId: (insight.priorityId || 1) as InsightColorId,
            resume: insight.resume,
            alerts: alert ? [{ id: alert.id, name: alert.name }] : [],
        };

        // Get base values from helper function
        const formValues = getEventEditInitialValues(eventEditData);

        // Set alertType from root level (it comes at root level from backend)
        // This needs to be set before the alert block since it's used even without alert
        // Note: templateType is used by AlertTypeSelector component for visual selection
        // The backend sends backendKey (e.g., "extreme_wind"), but AlertTypeSelector expects risk ID (e.g., "vendaval")
        const alertTypeValue = response.alertType || "extreme_wind";
        formValues.alertType = alertTypeValue;

        // Convert backendKey to risk ID for AlertTypeSelector
        const riskEntry = Object.entries(RISK_TYPES).find(
            ([_, risk]) => risk.backendKey === alertTypeValue
        );
        formValues.templateType = riskEntry ? riskEntry[0] : alertTypeValue;

        // Set area for message preview based on location type
        // Priority: 
        // 1. If locationName exists = use named location
        // 2. If geofence has single point = custom point, use coordinates
        // 3. If stationLatitude/stationLongitude exist = use coordinates as fallback
        // 4. If geofence has multiple points = area polygon
        const hasCoordinates = response.stationLatitude && response.stationLongitude;
        const isSinglePointGeofence = response.geofence && response.geofence.length === 1;

        if (eventEditData.locationName) {
            // Named location
            formValues.area = eventEditData.locationName;
        } else if (isSinglePointGeofence) {
            // Single point in geofence = custom point coordinates
            const point = response.geofence![0];
            formValues.area = `${point.lat.toFixed(4)}, ${point.lng.toFixed(4)}`;
        } else if (hasCoordinates) {
            // Fallback: use coordinates when no location name is available
            formValues.area = `${response.stationLatitude!.toFixed(4)}, ${response.stationLongitude!.toFixed(4)}`;
        } else if (response.geofence && response.geofence.length > 1) {
            // Multiple points = area polygon
            formValues.area = t("monitoredArea") as string;
        }

        // Map sensor alert config if present
        const sensorAlertConfig = mapSensorAlertConfig(response);
        if (sensorAlertConfig) {
            formValues.sensorAlertConfig = sensorAlertConfig;
        }

        // Override with alert-specific data if alert exists
        if (alert) {
            formValues.channels = alert.channels || ["whatsapp"];
            // EditAlertFormValues expects selectedUsers as number[] (userIds)
            // Only recipients with non-null id are actually selected;
            // the backend returns ALL users but marks selected ones with a numeric id.
            formValues.selectedUsers = alert.recipients
                ?.filter(r => r.id !== null)
                .map(r => r.userId) || [];
            formValues.block2Active = true;

            // Map notification data if available
            const notification = alert.notifications?.[0];
            if (notification) {
                formValues.customTitle = notification.title || "";
                formValues.customDescription = notification.description || "";
                formValues.minAdvanceTime = notification.minAdvanceTime ?? DEFAULT_MIN_ADVANCE_TIME;
                formValues.maxAdvanceTime = notification.maxAdvanceTime ?? LEGACY_MAX_ADVANCE_TIME_FALLBACK;
                formValues.minimumEventDuration = notification.minimumEventDuration ?? 60;

                // Map receipt types - always set this to preserve user's choice
                // even if both sendUpdates and sendCancellations are false
                const receiptTypes: string[] = [];
                if (notification.sendUpdates !== false) receiptTypes.push("sendUpdates");
                if (notification.sendCancellations !== false) receiptTypes.push("sendCancellations");
                formValues.receiptType = receiptTypes;
            }
        }

        return formValues;
    }, [mapConditionGroups, mapSensorAlertConfig]);

    // Load event details when drawer opens
    useEffect(() => {
        const loadEventDetails = async () => {
            if (!isOpen || !eventToEdit) return;

            setIsLoading(true);

            try {
                // Load unit measurements
                unitMeasurementStore.getUserUnitMeasurementApi();

                // Load dashboards (only if not already loaded)
                if (operationRuleStore.operationRules.length === 0) {
                    await operationRuleStore.getOperationRulesApi();
                }

                // Load users for alert block (await to ensure user list is ready
                // before syncing recipients from the API response below)
                await users.loadAlertUsers(i18n?.language);

                // Load alert templates (for badge/reset functionality)
                if (insightAlertStore.alertTemplates.length === 0) {
                    await insightAlertStore.getAlertTemplateConfig();
                }

                // Fetch event details from the unified endpoint
                const response = await eventService.getEventById(eventToEdit.id);

                if (response) {
                    setEventData(response);

                    // Set macroRegionOrigin in store for template lookup
                    if (response.macroRegionOrigin) {
                        insightAlertStore.setMacroRegionOrigin(response.macroRegionOrigin);

                        // Load environmental variables BEFORE rendering the form
                        // This ensures the AlertTypeSelector can correctly determine which risk categories are available
                        // Include station data when available so the API returns station-specific variables
                        const envParams: {
                            macroRegionOrigin: string;
                            stationId?: number;
                            latitude?: number;
                            longitude?: number;
                        } = { macroRegionOrigin: response.macroRegionOrigin };

                        // Pass stationId for existing stations (stationId > 0)
                        if (response.stationId && response.stationId > 0) {
                            envParams.stationId = response.stationId;
                        }
                        // Pass lat/lon for custom points (no stationId but has coordinates)
                        else if (response.stationLatitude !== undefined && response.stationLongitude !== undefined) {
                            envParams.latitude = response.stationLatitude;
                            envParams.longitude = response.stationLongitude;
                        }

                        await insightAlertStore.getEnvironmentalVariables(envParams);
                    }

                    // Get dashboard info
                    const dashboard = operationRuleStore.operationRules.find(
                        (d) => d.id === response.insight.dashboardId
                    );

                    // Map response to form values, passing locationName from props
                    const formValues = mapResponseToFormValues(response, dashboard, locationName);

                    // When reopening the edit drawer after the user confirmed a new sensor
                    // selection in Forecast Charts, Formik's enableReinitialize would otherwise
                    // reset sensorAlertConfig.sensors to the API-fetched (old) values.
                    // Override sensors in formValues with the store's current selection so
                    // the new selection survives re-initialization.
                    if (isReturningFromSensorSelection && insightAlertStore.selectedSensorIds.length > 0 && insightAlertStore.sensorMeasuredData.length > 0) {
                        const newSensorIds = insightAlertStore.selectedSensorIds;
                        const sensorMap = new Map(
                            insightAlertStore.sensorMeasuredData.map(s => [s.station_id, s]),
                        );
                        formValues.sensorAlertConfig = {
                            sensors: newSensorIds.map(id => {
                                const s = sensorMap.get(id);
                                return {
                                    stationId: id,
                                    regionId: s?.region_id ?? 0,
                                    stationName: s?.station_name ?? "",
                                };
                            }),
                            conditions: formValues.sensorAlertConfig?.conditions ?? [],
                            alertFrequency: formValues.sensorAlertConfig?.alertFrequency ?? 60,
                        };
                    }

                    setInitialValues(formValues);

                    // Pre-populate insightAlertStore with sensor selection so
                    // useSensorAlertSection can detect edit mode and transition
                    // directly to configuration state (FR-027, FR-029).
                    // Skip when returning from Forecast Charts sensor selection:
                    // the store already holds the new IDs committed by onConfirm
                    // and overwriting them would revert the user's new selection.
                    if (!isReturningFromSensorSelection && response.sensorAlertConfig?.sensors?.length) {
                        const sensorIds = response.sensorAlertConfig.sensors.map(
                            (s) => s.stationId
                        );
                        insightAlertStore.setSelectedSensorIds(sensorIds);
                        // Conditions are per-sensor; use first sensor as the shared conditions
                        const sensorConditions = response.sensorAlertConfig.sensors[0].conditions;
                        if (sensorConditions?.length) {
                            insightAlertStore.setSensorConditions(sensorConditions);
                        }
                    }

                    // Sync recipients from API to the user selection table and MobX store.
                    // Without this, usersTableSelected stays empty (since skipAutoSelect is true)
                    // and the table won't show the correct checkboxes.
                    // Only recipients with non-null id are actually selected;
                    // the backend returns ALL users but marks selected ones with a numeric id.
                    if (response.alert?.recipients && response.alert.recipients.length > 0) {
                        const selectedRecipients = response.alert.recipients.filter(r => r.id !== null);
                        const recipientUserIds = selectedRecipients.map(r => r.userId);
                        users.setUsersTableSelected(recipientUserIds);
                        // Map to IOperationRuleAlertUser[] (coerce nullable id)
                        const mappedRecipients = selectedRecipients.map(r => ({
                            id: r.id ?? 0,
                            userId: r.userId,
                            userName: r.userName,
                            isWhatsappAllowed: r.isWhatsappAllowed,
                            isPushAllowed: r.isPushAllowed,
                            isEmailAllowed: r.isEmailAllowed,
                        }));
                        insightAlertStore.setOperationRuleAlertUserSelected(mappedRecipients);
                    }
                }
            } catch (error) {
                console.error("Error loading event details:", error);
            } finally {
                setIsLoading(false);
            }
        };

        loadEventDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, eventToEdit]);

    // Handle form submission
    const handleSubmit = useCallback(
        async (values: EditEventFormValues) => {
            if (!eventData) return;

            setIsSaving(true);

            try {
                // Build condition groups for update
                const conditionGroups = values.conditionGroups?.map((group) => ({
                    id: group.id,
                    parentId: group.parentId ?? null,
                    groupOperator: (group.groupOperator || "and").toLowerCase(),
                    conditionOperator: (group.conditionOperator || "and").toLowerCase(),
                    conditions: group.conditions?.map((c) => ({
                        environmentalVariable: c.environmentalVariable,
                        environmentalVariableId: c.environmentalVariableId,
                        comparisonOperator: c.comparisonOperator,
                        value: c.value,
                    })) || [],
                })) || [];

                // Build update payload
                // Map templateType to backendKey for alertType
                // When restrictionType is false (Permissive), always use "no_category"
                const alertTypeBackendKey = (() => {
                    if (values.restrictionType === false) {
                        return "no_category";
                    }
                    const templateType = values.templateType || values.alertType;
                    if (templateType) {
                        const riskType = RISK_TYPES[templateType as RiskTypeId];
                        return riskType?.backendKey || templateType;
                    }
                    return eventData.alertType || "extreme_wind";
                })();

                const updatePayload: any = {
                    name: values.name,
                    conditionGroups,
                    alertType: alertTypeBackendKey,
                    insight: {
                        priorityId: values.insightColor,
                        restrictionType: values.restrictionType,
                        // Always send user-typed resume (empty string if not provided)
                        resume: values.resume ?? "",
                    },
                };

                // Handle alert: create/update or delete
                if (values.block2Active) {
                    // User wants alerts - create or update
                    // Build full recipient objects from selectedUsers (which are userIds)
                    // Use insightAlertStore.operationRuleAlertUserSelected for full user data
                    const recipients = insightAlertStore.operationRuleAlertUserSelected.length > 0
                        ? insightAlertStore.operationRuleAlertUserSelected.map(user => ({
                            id: user.id ?? 0,
                            userId: user.userId,
                            userName: user.userName,
                            isWhatsappAllowed: user.isWhatsappAllowed ?? false,
                            isPushAllowed: user.isPushAllowed ?? false,
                            isEmailAllowed: user.isEmailAllowed ?? false,
                        }))
                        : values.selectedUsers.map((userId: number) => {
                            // Fallback: find user in alertUsers list
                            const user = users.alertUsers.find(u => u.userId === userId);
                            return {
                                id: user?.id ?? 0,
                                userId: userId,
                                userName: user?.userName ?? "",
                                isWhatsappAllowed: user?.isWhatsappAllowed ?? false,
                                isPushAllowed: user?.isPushAllowed ?? false,
                                isEmailAllowed: user?.isEmailAllowed ?? false,
                            };
                        });

                    updatePayload.alert = {
                        recipients,
                        notifications: [{
                            id: eventData.alert?.notifications?.[0]?.id ?? 0,
                            title: values.customTitle || "",
                            description: values.customDescription || "",
                            type: "before",
                            minutesType: values.minAdvanceTime,
                            minAdvanceTime: values.minAdvanceTime,
                            maxAdvanceTime: values.maxAdvanceTime,
                            minimumEventDuration: values.minimumEventDuration,
                            sendUpdates: values.receiptType?.includes("sendUpdates") ?? true,
                            sendCancellations: values.receiptType?.includes("sendCancellations") ?? true,
                        }],
                        channels: values.channels || ["whatsapp"],
                        alertType: alertTypeBackendKey,
                    };
                } else if (eventData.alert) {
                    // User disabled alerts but event had an alert - delete it
                    updatePayload.alert = null;
                }
                // If block2Active is false and there was no alert, don't send alert field

                // Sensor alert config (3-state):
                // - undefined → omit (preserves existing backend records)
                // - null → signals backend to delete existing sensor records
                // - { sensors?, conditions?, lightning? } → create/update
                //   (spec 043: rain-only, lightning-only, or both)
                if (values.sensorAlertConfig !== undefined) {
                    const cfg = values.sensorAlertConfig;
                    const hasRain = !!cfg?.sensors?.length;
                    const hasLightning = !!cfg?.lightning;

                    if (hasRain || hasLightning) {
                        // Spec 043 §FR-014a — anchor preservation. The FE never
                        // mutates the loaded anchor; it forwards it verbatim iff
                        // the user has not moved the lightning point.
                        let lightningPayload = cfg.lightning;
                        if (hasLightning && cfg.lightning) {
                            const loaded = eventData.sensorAlertConfig?.lightning;
                            const pointUnchanged =
                                !!loaded &&
                                loaded.point.lat === cfg.lightning.point.lat &&
                                loaded.point.lng === cfg.lightning.point.lng;
                            lightningPayload = {
                                ...cfg.lightning,
                                anchor:
                                    pointUnchanged && loaded?.anchor
                                        ? loaded.anchor
                                        : undefined,
                            };
                        }
                        updatePayload.sensorAlertConfig = {
                            ...cfg,
                            sensors: cfg.sensors ?? [],
                            conditions: cfg.conditions ?? [],
                            lightning: lightningPayload,
                        };
                    } else {
                        // Both slices absent → cascade-delete all alert rows.
                        updatePayload.sensorAlertConfig = null;
                    }
                }

                // Update event using the new unified endpoint
                await eventService.updateEvent(eventData.insight.id, updatePayload);

            } catch (error) {
                console.error("Error updating event:", error);
                throw error;
            } finally {
                setIsSaving(false);
            }
        },
        [eventData, eventService]
    );

    // Reset form when drawer closes
    const resetForm = useCallback(() => {
        setEventData(null);
        // Clear user selection state so stale recipients don't persist across edit sessions
        users.clearUserTableSelected();
        // Clear sensor selection state so it doesn't leak into a new edit session
        insightAlertStore.clearSensorSelection();
        setInitialValues({
            name: "",
            alertType: "extreme_wind",
            channels: ["whatsapp"],
            selectedUsers: [],
            customTitle: "",
            customDescription: "",
            resume: "",
            minAdvanceTime: DEFAULT_MIN_ADVANCE_TIME,
            maxAdvanceTime: LEGACY_MAX_ADVANCE_TIME_FALLBACK,
            minimumEventDuration: 60,
            receiptType: ["sendUpdates", "sendCancellations"],
            conditionGroups: [],
            block1Active: true,
            block2Active: false,
            restrictionType: true,
            insightColor: 1,
        });
    }, [users.clearUserTableSelected]);

    // Compute location data for MonitoredLocationPreview
    const locationData = useMemo(() => {
        if (!eventData) return null;

        // Check if it's a point location (has station coordinates)
        if (eventData.stationId && eventData.stationLatitude && eventData.stationLongitude) {
            return {
                name: locationName,
                station: {
                    id: eventData.stationId,
                    name: locationName,
                    lat: eventData.stationLatitude,
                    lon: eventData.stationLongitude,
                    regionId: eventData.regionId || 0,
                },
                geofence: undefined,
            };
        }

        // Check if it's an area location (has geofence)
        if (eventData.geofence && eventData.geofence.length > 0) {
            return {
                name: locationName,
                station: undefined,
                geofence: eventData.geofence,
            };
        }

        return null;
    }, [eventData, locationName]);

    /**
     * Get the template conditions for the current risk type
     * Used to show the i4cast badge and reset button in edit mode
     */
    const templateConditions = useMemo(() => {
        if (!eventData || !eventData.alertType || !eventData.macroRegionOrigin) {
            return null;
        }

        const template = insightAlertStore.getTemplateForRisk(
            eventData.alertType,
            eventData.macroRegionOrigin
        );

        if (!template?.conditions || template.conditions.length === 0) {
            return null;
        }

        // template.conditions is already in ConditionGroup[] format
        return template.conditions;
    }, [eventData, insightAlertStore]);

    return {
        t,
        isLoading,
        isSaving,
        initialValues,
        validationSchema,
        eventData,
        handleSubmit,
        resetForm,
        // Users
        usersTableSelected: users.usersTableSelected,
        setUsersTableSelected: users.setUsersTableSelected,
        alertUsers: users.alertUsers,
        selectCurrentUser: users.selectCurrentUser,
        // Environmental variables for conditions
        environmentalVariables: insightAlertStore.environmentalVariables,
        // Location data for MonitoredLocationPreview
        locationData,
        // Template conditions for badge/reset functionality
        templateConditions,
    };
}
